import type { UserAccountContext } from '@/api/v1/auth/get-user-account-context/get-user-account-context.types'
import { logger } from '@/lib/logger'
import { languageToLocale } from '@/lib/utils/language-to-locale'

import { analytics } from './analytics'
import { AuthEvents } from './events'

let pendingAliasFromEmail: string | null = null

/**
 * Identify user in analytics right after registration, before an account
 * exists. Uses email as the temporary distinct id so events fired during the
 * welcome flow are attributed to the user. When the account is created and
 * `identifyUser` runs, it aliases this email to the real user id.
 */
export const identifyUserPreAccount = (params: {
  email: string
  name: string
  language: string
}): void => {
  try {
    analytics.identify(params.email)
    analytics.setUserProperties({
      email: params.email,
      name: params.name,
      language: params.language,
    })
    pendingAliasFromEmail = params.email
    logger.info('Pre-account user identified in analytics', {
      email: params.email,
    })
  } catch (error) {
    logger.error('Failed to pre-account identify user in analytics', { error })
  }
}

/**
 * Identify user in analytics with account context information
 * Should be called after login or account switch
 */
export const identifyUser = (context: UserAccountContext): void => {
  try {
    const {
      userAccountInformation,
      accountInformation,
      subscriptionInformation,
    } = context

    if (pendingAliasFromEmail) {
      if (pendingAliasFromEmail === userAccountInformation.email) {
        analytics.alias(userAccountInformation.id, pendingAliasFromEmail)
      } else {
        logger.info('Skipping analytics alias: pre-account email mismatch', {
          pendingEmail: pendingAliasFromEmail,
          identifiedEmail: userAccountInformation.email,
        })
      }
      pendingAliasFromEmail = null
    }

    analytics.identify(userAccountInformation.id)

    analytics.setUserProperties({
      email: userAccountInformation.email,
      name: userAccountInformation.name,
      user_role: userAccountInformation.role,
      account_id: accountInformation.id,
      account_name: accountInformation.name,
      account_type: accountInformation.type,
      is_managed: accountInformation.isManaged,
      // Only ever set to true, matching the backend writer's semantics
      // ("owns an account"): a person who owns account A stays an owner
      // while switched into account B, so false must never overwrite it.
      ...(userAccountInformation.isAccountOwner && { is_account_owner: true }),
      plan_code: subscriptionInformation?.planCode || null,
      has_active_subscription: context.hasActiveSubscription,
      has_free_plan: Boolean(context.freePlanInformation),
      language: languageToLocale(userAccountInformation.language),
      tenant_id: context.tenantInformation.id,
      tenant_code: context.tenantInformation.code,
    })

    logger.info('User identified in analytics', {
      userId: userAccountInformation.id,
      email: userAccountInformation.email,
    })
  } catch (error) {
    logger.error('Failed to identify user in analytics', { error })
  }
}

/**
 * Track user login event
 */
export const trackUserLogin = (context: UserAccountContext): void => {
  const {
    userAccountInformation,
    accountInformation,
    subscriptionInformation,
  } = context

  analytics.track(AuthEvents.USER_LOGGED_IN, {
    user_id: userAccountInformation.id,
    email: userAccountInformation.email,
    role: userAccountInformation.role,
    account_id: accountInformation.id,
    account_name: accountInformation.name,
    plan_code: subscriptionInformation?.planCode || null,
    has_active_subscription: context.hasActiveSubscription,
    timestamp: new Date().toISOString(),
  })
}

/**
 * Track account switch event
 */
export const trackAccountSwitch = (context: UserAccountContext): void => {
  const {
    userAccountInformation,
    accountInformation,
    subscriptionInformation,
  } = context

  analytics.track(AuthEvents.ACCOUNT_SWITCHED, {
    user_id: userAccountInformation.id,
    email: userAccountInformation.email,
    role: userAccountInformation.role,
    account_id: accountInformation.id,
    account_name: accountInformation.name,
    plan_code: subscriptionInformation?.planCode || null,
    has_active_subscription: context.hasActiveSubscription,
    timestamp: new Date().toISOString(),
  })
}

/**
 * Track user logout event and reset analytics
 */
export const trackUserLogout = (): void => {
  try {
    analytics.track(AuthEvents.USER_LOGGED_OUT, {
      timestamp: new Date().toISOString(),
    })
    analytics.reset()
    pendingAliasFromEmail = null
    logger.info('User logged out and analytics reset')
  } catch (error) {
    logger.error('Failed to track user logout', { error })
  }
}
